/* The church's Administration menu.
 *
 * The client's page: once the Super Admin approves a church's admin, "the
 * Admin of the group will be getting the Administration menu for his group".
 * These are the screens that run a church rather than take part in it. The
 * sidebar draws them under that heading; on a phone, where there is no
 * sidebar, the account menu opens /administration with the same list.
 *
 * Each one shows only to somebody the server would let use it. The server
 * checks again on every call, so this decides what is worth showing, not what
 * is allowed.
 */
import {
  ClipboardCheck,
  Contact,
  ListChecks,
  Phone,
  Send,
  Shield,
  type LucideIcon,
} from 'lucide-react'
import { useFeatures, useMe, useMyPermissions } from '@/api/queries'

export type AdminItem = {
  label: string
  icon: LucideIcon
  to: string
  /** One line for the phone list, where there is room to say what it is. */
  blurb: string
}

export function useAdminItems(): AdminItem[] {
  const { data: me } = useMe()
  const permissions = useMyPermissions(me?.church_id ?? null)
  const features = useFeatures()
  if (!me?.church_id) return []

  const held = permissions.data ?? []
  const admin = me.role === 'church_admin' || me.role === 'super_admin'
  /* A ROLE IS NOT ONLY church_admin. Somebody given a Moderator role with
     members.view may use Members and Permissions too. */
  const seesMembers = admin || held.includes('members.view')
  const keepsAttendance =
    admin || held.some((p) => p === 'attendance.mark' || p === 'attendance.manage')
  const managesQuestions = admin || held.includes('members.questions')

  const items: (AdminItem | false)[] = [
    seesMembers && {
      label: 'Members', icon: Contact, to: '/members',
      blurb: 'Everybody in the church, and their groups',
    },
    seesMembers && {
      label: 'Permissions', icon: Shield, to: '/permissions',
      blurb: 'Moderators and the roles they hold',
    },
    managesQuestions && {
      label: 'Join questions', icon: ListChecks, to: '/join-questions',
      blurb: 'What people are asked when they join',
    },
    seesMembers && features.anyMessaging && {
      label: 'Messages', icon: Send, to: '/messages',
      blurb: 'Write to the church, a group or chosen people',
    },
    seesMembers && features.anyMeetings && {
      label: 'Meetings', icon: Phone, to: '/calls',
      blurb: 'Arrange calls and lives',
    },
    keepsAttendance && {
      label: 'Attendance', icon: ClipboardCheck, to: '/attendance',
      blurb: 'Mark who came, and see who has been away',
    },
  ]
  return items.filter((i): i is AdminItem => Boolean(i))
}
